import { GetServerSidePropsContext } from 'next';
import { DynamicPageProps } from '~/pages/[[...slug]]';
import { getIsInitialLoad } from '~/templates/pages/utils/isInitialLoad';
import { dehydrate, QueryClient } from 'react-query';
import { DynamicPage, productSetDataFetcher } from '~/templates/pages';
import { staticDataResolver } from '~/templates/pages/utils';
import { dataPreFetcher, getHeaders, handleSSRResponse } from '~/templates/pages/utils/dataFetcher';

export default function ProductSetDetailPage(props: DynamicPageProps) {
    const { ...rest } = props;
    return <DynamicPage {...rest} />;
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    const { req, res, locale = '', resolvedUrl, params } = context;

    // Extract productIds from the URL parameter and split by "-"
    const rawProductIds = (params?.productIds as string) || '';

    const { host = '' } = req.headers;
    const queryClient = new QueryClient();
    const isInitialLoad = getIsInitialLoad(context);

    const headers = await getHeaders(req);

    const { page, frame, franchise } = await dataPreFetcher({
        host,
        locale,
        headers,
        queryClient,
        path: '/ps',
    });

    const { settings } = staticDataResolver({ franchise, frame, headers, isInitialLoad });
    const { queryKey, franchiseAPIPath } = franchise;

    const productSet = await productSetDataFetcher({
        productIds: rawProductIds,
        franchiseAPIPath,
        queryKey,
        queryClient,
        headers,
    });

    // Handle SSR response logic (headers, redirects, status codes, cache-control)
    const ssrResult = handleSSRResponse({
        res,
        pageResponse: page,
        status: productSet?.statusCode || 200,
        resolvedUrl,
    });

    if (ssrResult?.redirect) {
        return ssrResult;
    }

    const dehydratedState = dehydrate(queryClient);
    // Clear the cache to avoid high memory consumption on server
    queryClient.clear();

    return {
        props: {
            dehydratedState,
            franchise,
            settings,
            pageBaseUrl: host?.includes('local') ? `http://${host}` : `https://${host}`,
            resolvedUrl,
            page: page?.data,
            locale,
        },
    };
};
